import { useEffect, useRef, useState } from 'react';
import { Tabs, ThemeProvider, Toast } from '@gupshup-frontend/gupshup-ts-components';
import { createBridge } from '../../bridge/bloomreach';
import { isLocalHost } from '../../bridge/origins';
import { useLaunchParams } from '../../state/launchParams';
import { getOrg } from '../../api/gsIntg';
import {
  buildPlaceholderDefinition, isPlaceholderDefinition, wasSavedByWidget, withoutConsentDecision,
} from '../../webhook/buildDefinition';
import Message from '../Message';
import AccountSettings from '../AccountSettings';
import Templates from '../Templates';
import Dashboard from '../Dashboard';
import AuthScreen from './AuthScreen';
import OutsideBloomreach from './OutsideBloomreach';
import { GUPSHUP_THEME_CONFIG, ERROR_COPY, ORG_TOKEN_STORAGE_KEY } from '../../utils/constants';
import gupshupLogo from '../../assets/gupshup-icon.png';

const readStoredOrgId = () => {
  try {
    return localStorage.getItem(ORG_TOKEN_STORAGE_KEY) || '';
  } catch {
    // Storage can be blocked (private mode, cross-origin iframe policy); fall back to session-only.
    return '';
  }
};

const DASHBOARD_ENABLED = process.env.REACT_APP_ENABLE_DASHBOARD === 'true';

// Fixed positions of the always-present tabs in WidgetShell's `tabs` array below (Dashboard is
// conditional and always last, so it never needs an index). Keep this in sync with that array's
// order — it's declared at module scope, not next to the array, so every consumer (the initial
// useState, the post-load effect, the onboarding CTA) can use it with no declaration-order risk.
const TAB_INDEX = { MESSAGE: 0, TEMPLATES: 1, ACCOUNT_SETTINGS: 2 };

// What Done and Test tell the marketer. No message and no template open: the node goes back as the placeholder. Unsaved work on
// screen: Done is refused (see onRequestState), and the hint says to Save, or to Cancel.
const NO_MESSAGE_HINT =
  'No message here yet. Bloomreach will ask you to confirm general consent. Until you pick a template and click Save, this node skips every customer.';
const NEVER_SAVED_HINT =
  "You picked a template but didn't click Save. Click Save, then Done. To leave without a message, click Cancel.";
const UNSAVED_HINT = 'You have unsaved changes. Click Save, then Done. To leave without saving them, click Cancel.';

const STATUS_LABEL = {
  connecting: 'Connecting to Bloomreach…',
  connected: 'Connected to Bloomreach',
  standalone: 'Standalone preview',
  rejected: 'Not supported',
};

// DFS for the first string leaf — matches Bloomreach's nested errors object shape.
function firstLeafMessage(node) {
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = typeof item === 'string' ? item : firstLeafMessage(item);
      if (found) return found;
    }
    return null;
  }
  if (node && typeof node === 'object') {
    for (const key of Object.keys(node)) {
      const found = firstLeafMessage(node[key]);
      if (found) return found;
    }
    return null;
  }
  return typeof node === 'string' ? node : null;
}

const Widget = () => {
  const launchParams = useLaunchParams();
  const paramOrgId = launchParams.orgId || '';
  // Lazy init: reads storage once, not on every render.
  const [storedOrgId, setStoredOrgId] = useState(readStoredOrgId);

  // The query param (Bloomreach's configured Widget URL) always wins over a stored orgId.
  const orgId = paramOrgId || storedOrgId;

  // The postMessage handshake owns Bloomreach's own loading spinner (it waits on
  // 'widget_initialized' before it will render this iframe at all), so it's created and
  // started here — unconditionally, before orgId is known — instead of inside WidgetShell.
  // Gating it on orgId left Bloomreach spinning forever whenever AuthScreen was showing.
  const [bridgeStatus, setBridgeStatus] = useState('connecting');
  const [incomingWidgetState, setIncomingWidgetState] = useState(null);
  const [errorToast, setErrorToast] = useState({ open: false, message: '', full: null });
  // stateWarning shows "nothing saved yet" in-page instead of only Bloomreach's error.
  const [stateWarning, setStateWarning] = useState({ open: false, message: '' });

  const messageRef = useRef(null);
  // lastMessageStateRef backstops messageRef, which goes null once Message's tab unmounts.
  const lastMessageStateRef = useRef(null);
  // Whether the marketer changed the node in this session. Until then Done hands back exactly what Bloomreach sent:
  // the copy the Message page rebuilds as it restores a saved node can differ from it (an older node takes the org's
  // consent), and Done would then check consent on a node nobody touched.
  const editedRef = useRef(false);
  // The hint of the last reply we refused, or null when the last reply was a normal one. Bloomreach echoes its consent failure
  // as an `errors` message; while this is set, the toast says what is unsaved instead of Bloomreach's consent message.
  const blockedRef = useRef(null);
  const incomingRef = useRef({ webhook: null, widget_state: null });

  const bridgeRef = useRef(null);
  if (!bridgeRef.current) {
    bridgeRef.current = createBridge({
      onHello: ({ webhook, widget_state }) => {
        incomingRef.current = { webhook: webhook ?? null, widget_state: widget_state ?? null };
        setIncomingWidgetState(widget_state ?? null);
        setBridgeStatus('connected');
      },
      onRequestState: () => {
        // Save commits everything, so Done is refused while the screen holds unsaved work: the reply is the node we would
        // hand back, with its consent decision withheld, and Bloomreach's own consent check keeps the editor open. Done, Test
        // and Cancel ask the same way. Cancel never validates, so it still leaves, and Test sends what this hands back.
        // A Message tab that is off screen has nothing unsaved to report.
        const unsaved = messageRef.current?.getUnsaved?.() ?? 'none';
        const draft = editedRef.current ? (messageRef.current?.getState?.() ?? lastMessageStateRef.current) : null;
        const { webhook, widget_state } = incomingRef.current;
        // The node we would hand back: the last message saved in this session, else what Bloomreach sent. Bloomreach opens a
        // fresh node with the preset's own webhook, not with null, and a preset can ship with general consent (Gupshup's does),
        // so consent cannot tell a fresh node from a saved one. Only what the widget wrote can (wasSavedByWidget). Any other
        // node gets the placeholder (general consent, a body that aborts every send): it cannot keep the preset's request, so
        // a Test or a scenario run can never send it.
        const saved = wasSavedByWidget(webhook, widget_state);
        const current = draft ?? (saved ? { webhook, widget_state } : { webhook: buildPlaceholderDefinition(), widget_state: null });

        if (unsaved !== 'none') {
          const hint = unsaved === 'never_saved' ? NEVER_SAVED_HINT : UNSAVED_HINT;
          blockedRef.current = hint;
          setStateWarning({ open: true, message: hint });
          return { webhook: withoutConsentDecision(current.webhook), widget_state: current.widget_state };
        }

        blockedRef.current = null;
        // Nothing is unsaved. A node with no message, or a reopened placeholder that comes back as it was, still has none to
        // send: the marketer is told so.
        if (!draft && (!saved || isPlaceholderDefinition(webhook))) setStateWarning({ open: true, message: NO_MESSAGE_HINT });
        return current;
      },
      onReject: () => setBridgeStatus('rejected'),
      onErrors: (errors) => {
        setErrorToast({
          open: true,
          // After a refused Done, Bloomreach's "Please select a consent category." is our doing: say what is unsaved instead.
          message: blockedRef.current || firstLeafMessage(errors) || 'Bloomreach reported an error.',
          full: errors,
        });
      },
      onStandalone: () => setBridgeStatus('standalone'),
    });
  }

  useEffect(() => {
    bridgeRef.current.start();
  }, []);

  // Nothing renders, and no API is called, until Bloomreach itself answers the handshake
  // (app_hello from a Bloomreach origin, see bridge/origins.js; the browser sets event.origin, so it can't be
  // faked). Opening the URL directly or framing it elsewhere never gets past this. Local
  // dev keeps the standalone preview.
  const insideBloomreach =
    bridgeStatus === 'connected' || (bridgeStatus === 'standalone' && isLocalHost());
  if (!insideBloomreach) {
    return bridgeStatus === 'connecting' ? (
      <div className="br-shell__center-message" data-testid="bloomreach-widget-connecting">
        <p>{STATUS_LABEL.connecting}</p>
      </div>
    ) : (
      <OutsideBloomreach />
    );
  }

  if (!orgId) {
    return (
      <AuthScreen
        onAuthenticated={(enteredOrgId) => {
          try {
            localStorage.setItem(ORG_TOKEN_STORAGE_KEY, enteredOrgId);
          } catch {
            // Best-effort persistence; the widget still works for this load either way.
          }
          setStoredOrgId(enteredOrgId);
        }}
      />
    );
  }

  return (
    <WidgetShell
      orgId={orgId}
      launchParams={launchParams}
      bridgeStatus={bridgeStatus}
      incomingWidgetState={incomingWidgetState}
      errorToast={errorToast}
      setErrorToast={setErrorToast}
      stateWarning={stateWarning}
      setStateWarning={setStateWarning}
      messageRef={messageRef}
      lastMessageStateRef={lastMessageStateRef}
      editedRef={editedRef}
    />
  );
};

const WidgetShell = ({
  orgId,
  launchParams,
  bridgeStatus,
  incomingWidgetState,
  errorToast,
  setErrorToast,
  stateWarning,
  setStateWarning,
  messageRef,
  lastMessageStateRef,
  editedRef,
}) => {
  const [orgState, setOrgState] = useState('loading'); // loading | missing | ready
  const [org, setOrg] = useState(null);
  const [selectedTab, setSelectedTab] = useState(TAB_INDEX.MESSAGE);
  // latestState survives Message unmounting on tab switch (Tabs unmounts inactive panels).
  const [latestState, setLatestState] = useState(null);
  const [showErrorDetail, setShowErrorDetail] = useState(false);

  const initialTabSetRef = useRef(false);

  // quiet swaps the fresh org in place. The loading state puts the skeleton where the tabs are, which unmounts the Message
  // tab and drops the template open in it, so a reload made while a message is being written has to be quiet. A quiet
  // reload that fails keeps the org already on screen.
  const loadOrg = async ({ quiet = false } = {}) => {
    if (!quiet) setOrgState('loading');
    try {
      const data = await getOrg(orgId);
      setOrg(data);
      setOrgState(data ? 'ready' : 'missing');
    } catch {
      // Real fetch failures (not a 404, which resolves to null) show 'missing' + a toast.
      if (!quiet) {
        setOrg(null);
        setOrgState('missing');
      }
      setErrorToast({ open: true, message: ERROR_COPY.unreachableService, full: null });
    }
  };

  useEffect(() => {
    loadOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const hasEnterpriseChannel = !!org?.channels?.some((channel) => channel.channelType === 'enterprise');
  const phoneAttribute = org?.config?.phoneAttribute || 'phone';

  useEffect(() => {
    if (orgState === 'loading' || initialTabSetRef.current) return;
    initialTabSetRef.current = true;
    setSelectedTab(hasEnterpriseChannel ? TAB_INDEX.MESSAGE : TAB_INDEX.ACCOUNT_SETTINGS);
  }, [orgState, hasEnterpriseChannel]);

  const needsOnboarding = orgState !== 'loading' && !hasEnterpriseChannel;

  // Order must match TAB_INDEX above (Message, Templates, Account Settings, [Dashboard]).
  const tabs = [
    {
      label: 'Message',
      'data-testid': 'bloomreach-widget-tab-message',
      content: (
        <Message
          ref={messageRef}
          orgId={orgId}
          org={org}
          launchParams={launchParams}
          phoneAttribute={phoneAttribute}
          reloadOrg={() => loadOrg({ quiet: true })}
          incomingWidgetState={latestState?.widget_state ?? incomingWidgetState}
          initialState={latestState}
          onStateChange={(state, meta) => {
            lastMessageStateRef.current = state;
            // The restore on open is not a change; anything else the page reports is.
            if (!meta?.restored) editedRef.current = true;
            setLatestState(state);
          }}
          onError={(message) => setErrorToast({ open: true, message, full: null })}
        />
      ),
    },
    {
      label: 'Templates',
      'data-testid': 'bloomreach-widget-tab-templates',
      // Management surface only — no ref, no bridge/Save-Done wiring, unlike the Message tab.
      content: <Templates orgId={orgId} org={org} />,
    },
    {
      label: 'Account Settings',
      'data-testid': 'bloomreach-widget-tab-account-settings',
      content: <AccountSettings orgId={orgId} org={org} reloadOrg={loadOrg} />,
    },
    ...(DASHBOARD_ENABLED
      ? [{ label: 'Dashboard', 'data-testid': 'bloomreach-widget-tab-dashboard', content: <Dashboard orgId={orgId} org={org} /> }]
      : []),
  ];

  return (
    <ThemeProvider config={GUPSHUP_THEME_CONFIG}>
      {/* Header + tab strip are both position:sticky (see overrides.css) so they stay visible. */}
      <div className="bloomreach-widget br-shell">
        <div className="br-shell__header">
          <div className="br-content br-shell__header-inner">
            <div className="br-shell__title-block">
              <span className="br-shell__title">Gupshup WhatsApp</span>
              <span className="br-shell__subtitle">for Bloomreach</span>
            </div>
            <span
              data-testid="bloomreach-widget-status-chip"
              className={`br-shell__status-pill br-shell__status-pill--${bridgeStatus}`}
            >
              <span className="br-shell__status-dot" />
              {STATUS_LABEL[bridgeStatus]}
            </span>
          </div>
        </div>

        <div className="br-content">
        {needsOnboarding && (
          <div
            data-testid="bloomreach-widget-onboarding-banner"
            className="br-shell__banner br-shell__banner--onboarding"
          >
            <span>Add your Gupshup HSM account to start</span>
            <button
              type="button"
              data-testid="bloomreach-widget-onboarding-cta"
              className="br-shell__banner-cta"
              onClick={() => setSelectedTab(TAB_INDEX.ACCOUNT_SETTINGS)}
            >
              Add channel
            </button>
          </div>
        )}

        {orgState === 'loading' ? (
          <div className="br-skeleton" data-testid="bloomreach-widget-loading">
            <div className="br-skeleton__row" />
            <div className="br-skeleton__row" />
            <div className="br-skeleton__row" />
          </div>
        ) : (
          <Tabs
            tabsType="contained"
            data={tabs}
            value={selectedTab}
            handleTabChange={(e, value) => setSelectedTab(value)}
          />
        )}
        </div>

        <Toast
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          data-testid="bloomreach-widget-error-toast"
          maxWidth="30vw"
          onClose={() => setErrorToast((prev) => ({ ...prev, open: false }))}
          title={errorToast.message}
          type="error"
          open={errorToast.open}
          autoHideDuration={5000}
        />

        <Toast
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          data-testid="bloomreach-widget-state-warning-toast"
          maxWidth="30vw"
          onClose={() => setStateWarning((prev) => ({ ...prev, open: false }))}
          title={stateWarning.message}
          type="warning"
          open={stateWarning.open}
          autoHideDuration={5000}
        />

        {errorToast.full && (
          <div className="br-shell__error-detail">
            <button
              type="button"
              data-testid="bloomreach-widget-errors-toggle"
              onClick={() => setShowErrorDetail((prev) => !prev)}
            >
              {showErrorDetail ? 'Hide' : 'Show'} Bloomreach error detail
            </button>
            {showErrorDetail && <pre data-testid="bloomreach-widget-errors-pre">{JSON.stringify(errorToast.full, null, 2)}</pre>}
          </div>
        )}

        <div
          className="br-shell__footer"
          data-testid="bloomreach-widget-footer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            height: 52,
            marginTop: 24,
            borderTop: '1px solid var(--br-rule)',
            fontSize: 12,
            fontWeight: 300,
            color: 'var(--br-muted)',
          }}
        >
          <span>Powered by</span>
          <img
            className="br-shell__footer-logo"
            src={gupshupLogo}
            alt="Gupshup"
            style={{ height: 18, width: 'auto' }}
          />
        </div>
      </div>
    </ThemeProvider>
  );
};

export default Widget;
